import Button from 'components/Form/Button';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { POST } from 'services/api';
import styled, { keyframes } from 'styled-components';

// --- Estilos de Componentes (unchanged) ---
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  &.closing {
    animation: fadeOut 0.3s forwards;
  }
`;

export const ModalContainer = styled.div`
  background-color: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  position: relative;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 95%;
    padding: 20px;
  }

  &.closing {
    animation: slideOut 0.3s forwards;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
  h2 {
    margin: 0;
    font-size: 24px;
    color: #333;
  }
`;

export const ModalContent = styled.div`
  padding: 0 10px;
`;

export const CompanyButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    button {
      width: 100%;
    }
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #ced4da;
  border-radius: 8px;
  box-sizing: border-box;
  font-size: 19px;
  background-color: #f8f9fa;
  margin-bottom: 20px;
  color: #333333;
  text-align: center;
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ error }) => (error ? 'red' : '#ced4da')};
  border-radius: 8px;
  box-sizing: border-box;
  font-size: 16px;
  background-color: #f8f9fa;
  margin-top: 15px;
  min-height: 100px;
  resize: vertical;
  margin-bottom: 20px;
  color: #333333;
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  margin-top: 5px;
  margin-bottom: 10px;
`;

const Loader = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite;
  margin: 20px auto;
`;

const LoaderText = styled.p`
  color: #333333;
  text-align: center;
  margin-top: 10px;
  font-size: 16px;
`;

// Componente Principal
export default function ModalAcoesDocumento({
  codPropostaExterna,
  onClose,
  isClosing = false,
  reloadContent,
  anexo,
  id,
  proposta,
  documento_expedicao,
}) {
  const [loading, setLoading] = useState(false);
  const [selectedAction, setSelectedAction] = useState('aprovar');
  const [observation, setObservation] = useState('');
  const [observationError, setObservationError] = useState(false);

  const actionTitles = {
    aprovar: 'Aprovar Documento de Expedição',
    reprovar: 'Reprovar Documento de Expedição',
  };

  const successMessages = {
    aprovar: `Proposta ${proposta} foi aprovada com sucesso!`,
    reprovar: `Proposta ${proposta} foi reprovada com sucesso!`,
  };

  const handleActionChange = (e) => {
    setSelectedAction(e.target.value);
    setObservationError(false);
    setObservation('');
  };

  const handleSave = async () => {
    if (selectedAction === 'reprovar' && !observation.trim()) {
      setObservationError(true);
      toast.error('O motivo da reprovação é obrigatório.');
      return;
    }

    if (observation.length > 500) {
      setObservationError(true);
      toast.error('O motivo da reprovação não pode exceder 500 caracteres.');
      return;
    }

    setObservationError(false);
    setLoading(true);

    try {
      let url, body;
      if (selectedAction === 'aprovar') {
        url = '/financing-desk-control/aprovaexpedicao';
        body = { proposta: proposta };
      } else {
        url = '/financing-desk-control/reprovaexpedicao';
        body = { proposta: proposta, motivo: observation };
      }
      //console.log(body)
      const result = await POST(url, body, true);
      //console.log(POST)
      if (result && result.status === 'ok') {
        toast.success(successMessages[selectedAction]);
        if (reloadContent) reloadContent();
        onClose();
      } else {
        const errorMessage =
          result?.erro || 'Erro desconhecido na resposta da API.';
        toast.error(`Erro ao atualizar a proposta: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Ocorreu um erro na requisição:', error);
      let errorMessage = 'Ocorreu um erro ao processar a requisição.';
      if (error.message.includes('Method Not Allowed')) {
        errorMessage =
          'Ação não permitida pelo servidor. Verifique se o método HTTP está correto (POST). Contate o suporte técnico.';
      } else if (error.message.includes('Unexpected token')) {
        errorMessage =
          'Resposta do servidor inválida. Contate o suporte técnico para verificar a configuração da API.';
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay className={isClosing ? 'closing' : ''} onClick={onClose}>
      <ModalContainer
        className={isClosing ? 'closing' : ''}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <h2>
            {actionTitles[selectedAction]} (ID:{proposta})
          </h2>
        </ModalHeader>
        <ModalContent>
          <div>
            <label htmlFor="documentAction">Escolha uma Ação:</label>
            <FormSelect
              id="documentAction"
              value={selectedAction}
              onChange={handleActionChange}
            >
              <option value="aprovar">Aprovar Documento</option>
              <option value="reprovar">Reprovar Documento</option>
            </FormSelect>
          </div>
          {selectedAction === 'reprovar' && (
            <>
              <label htmlFor="observation">Motivo da Reprovação</label>
              <FormTextarea
                id="observation"
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                error={observationError}
                placeholder="Digite o motivo da reprovação aqui..."
              />
              {observationError && (
                <ErrorMessage>
                  {observation.length > 500
                    ? 'O motivo não pode exceder 500 caracteres.'
                    : 'O motivo é obrigatório para reprovar.'}
                </ErrorMessage>
              )}
            </>
          )}
          {loading && (
            <>
              <Loader />
              <LoaderText>Processando...</LoaderText>
            </>
          )}
        </ModalContent>
        <CompanyButtonContainer>
          <Button onClick={onClose} color="lightgrey" border nospace>
            Cancelar
          </Button>
          <Button onClick={handleSave} loading={loading} color="blue" nospace>
            {loading ? 'Processando...' : 'Salvar'}
          </Button>
        </CompanyButtonContainer>
      </ModalContainer>
    </Overlay>
  );
}
